import { observer } from 'mobx-react-lite';

import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { useEffect } from 'react';
import { StockIndexResponse, StockResponse } from '../../../response/responseSchema';
import modalStore from '@client/stores/modalStore';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import ChangeBarGraph, { ChangeBarGraphItem } from './ChangeBarGraph';
import TargetReturnButton from './TargetReturnButton';

class ViewStore {
    gainers: StockResponse[] = [];
    losers: StockResponse[] = [];
    indexPerformance: StockIndexResponse[] = [];
    briefDate: string | null = null;

    targetReturn: 'daily' | 'mtd' | 'ytd' = 'daily';

    graphItems: ChangeBarGraphItem<{ ticker: string }>[] = [];

    isLoading: boolean = true;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        runInAction(() => {
            this.isLoading = true;
        });

        try {
            const [gainers, losers, indexPerformance] = await Promise.all([
                apiClient.stockIndex.public.getTopGainerLoser.$query({
                    gainerOrLoser: 'gainer',
                    targetReturn: this.targetReturn,
                }),
                apiClient.stockIndex.public.getTopGainerLoser.$query({
                    gainerOrLoser: 'loser',
                    targetReturn: this.targetReturn,
                }),
                apiClient.stockIndex.public.getIndexPerformance.$query(),
            ]);

            runInAction(() => {
                this.gainers = gainers as StockResponse[];
                this.losers = losers as StockResponse[];
                this.indexPerformance = indexPerformance;
                this.briefDate = _.last(indexPerformance)?.price?.date || null;

                this.makeGraphItems();
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    private makeGraphItems() {
        // S&P 500 변화율 추출
        const sp500Index = this.indexPerformance.find((index) => index.ticker === '_SP500');
        let sp500Return = 0;
        if (this.targetReturn === 'daily') {
            sp500Return = Number(sp500Index?.price?.dailyReturn) || 0;
        } else if (this.targetReturn === 'mtd') {
            sp500Return = Number(sp500Index?.price?.mtdReturn) || 0;
        } else if (this.targetReturn === 'ytd') {
            sp500Return = Number(sp500Index?.price?.ytdReturn) || 0;
        }

        // 상승 종목 (서버에서 5개 제한)
        const gainerItems = this.gainers.map((holding) => {
            let targetReturn = 0;
            if (this.targetReturn === 'daily') {
                targetReturn = Number(holding.price?.dailyReturn) || 0;
            } else if (this.targetReturn === 'mtd') {
                targetReturn = Number(holding.price?.mtdReturn) || 0;
            } else if (this.targetReturn === 'ytd') {
                targetReturn = Number(holding.price?.ytdReturn) || 0;
            }

            return {
                name: holding.ticker,
                changePercent: targetReturn,
                data: { ticker: holding.ticker },
            };
        });

        // 하락 종목 (서버에서 5개 제한, 가장 많이 하락한 것이 맨 아래로)
        const loserItems = this.losers
            .map((holding) => {
                let targetReturn = 0;
                if (this.targetReturn === 'daily') {
                    targetReturn = Number(holding.price?.dailyReturn) || 0;
                } else if (this.targetReturn === 'mtd') {
                    targetReturn = Number(holding.price?.mtdReturn) || 0;
                } else if (this.targetReturn === 'ytd') {
                    targetReturn = Number(holding.price?.ytdReturn) || 0;
                }

                return {
                    name: holding.ticker,
                    changePercent: targetReturn,
                    data: { ticker: holding.ticker },
                };
            })
            .reverse();

        this.graphItems = [
            ...gainerItems,
            {
                name: 'S&P500',
                changePercent: sp500Return,
                isBenchmark: true,
            },
            ...loserItems,
        ];
    }

    changeTargetReturn(target: 'daily' | 'mtd' | 'ytd') {
        this.targetReturn = target;
        this.fetch();
    }

    get highlightPercent() {
        return this.targetReturn === 'daily' ? 5 : this.targetReturn === 'mtd' ? 15 : 30;
    }
}

const TopGainerLoserBriefCard = observer(() => {
    const viewStore = useMobxInstance(ViewStore);

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    return (
        <div>
            <ChangeBarGraph<{ ticker: string }>
                items={viewStore.graphItems}
                highligthPercent={viewStore.highlightPercent}
                className="-mx-5 mt-4 grow justify-between gap-2"
                keepItemOrder={true}
                isShowArrow={(item) => {
                    return !item.isBenchmark;
                }}
                onClickBar={(item) => {
                    if (item.isBenchmark) {
                        return;
                    }

                    const ticker = item.data?.ticker;
                    if (ticker) {
                        modalStore.stock({
                            ticker,
                        });
                    }
                }}
            />
            {viewStore.briefDate && (
                <TargetReturnButton
                    className="mt-8"
                    targetDate={viewStore.briefDate}
                    targetReturn={viewStore.targetReturn}
                    onChangeTargetReturn={(target) => viewStore.changeTargetReturn(target)}
                />
            )}
        </div>
    );
});

export default TopGainerLoserBriefCard;
